import { AxiosResponse } from "axios";
import { MERCHANT_V3_API_URLS as M3_API } from "@/apps/merchant3/services/apiClient/apiUrl";
import { jwtApiClient } from "@/apps/merchant3/services/apiClient/apiClient";
import { ICartWrap } from "@/apps/merchant3/models/IStateCart";
import {
  IOffersResponse,
  IpreRegDevicesResponse,
  IPreRegPricing,
} from "@/apps/merchant3/models/IStateOffers";
import ISubmitCartResponse from "@/apps/merchant3/types/ISubmitCartResponse";
import ICartStatus from "@/apps/merchant3/models/ICartStatus";
import { ProductVersion } from "@/apps/merchant3/types/ProductVersion";
import IFraudCheckResponse from "@/apps/merchant3/types/IFraudCheckResponse";
import IIncodeLivenessResponse from "@/apps/merchant3/types/IIncodeLivenessResponse";
import IGetPhoneSelfieStatusResponse from "@/apps/merchant3/types/IGetPhoneSelfieStatusResponse";
import { ICartAccessoryType } from "@/apps/merchant3/types/CartAccessories";

export interface ICartTagWrap {
  cartTag: string;
}
export interface IManufacturer {
  name: string;
  numberOfDeviceFamilies: number;
}

export interface IRecommendedOffers {
  deviceFamilyId: number;
  deviceModelId: string;
  friendlyDeviceName: string;
  manufacturer: string;
  minDownPayment: number;
  modelSelect: string;
  retailPrice: number | string;
  description?: string;
}

export interface ISelfServiceOffers {
  manufacturer: string;
  family: string;
  model: string;
  downPayment: number;
  totalCost: number;
  weeklyCost: number;
}

export interface IManufacturerWrap {
  manufacturers: IManufacturer[];
  recommendedOffers?: IRecommendedOffers[];
  offersWithDiscount?: boolean;
  selfServiceOffers?: ISelfServiceOffers[];
}

export const getOffers = async (
  cartTag: string,
  manufacturer?: string | null
): Promise<AxiosResponse<IOffersResponse>> => {
  return await jwtApiClient.get(M3_API(cartTag).OFFERS, {
    params: {
      manufacturer: manufacturer || null,
    },
  });
};

export const putOffers = async (
  cartTag: string,
  months: number
): Promise<AxiosResponse<IOffersResponse>> => {
  return await jwtApiClient.put(
    M3_API(cartTag).SUBMIT_OFFERS,
    {},
    {
      params: {
        months: months,
      },
    }
  );
};

export const getPreRegistrationManufacturer = async (
  cartTag: string
): Promise<AxiosResponse<IManufacturerWrap>> => {
  return await jwtApiClient.get(M3_API(cartTag).PRE_REGISTRATION_MANUFACTURER);
};

export const getPreRegistrationDevices = async (
  cartTag: string,
  manufacturer: string
): Promise<AxiosResponse<IpreRegDevicesResponse>> => {
  return await jwtApiClient.get(M3_API(cartTag).PRE_REGISTRATION_DEVICES, {
    params: {
      manufacturer: manufacturer,
    },
  });
};

export const getPreRegistrationPaymentTerms = async (
  cartTag: string,
  deviceModelId: string
): Promise<AxiosResponse<IPreRegPricing>> => {
  return await jwtApiClient.get(
    M3_API(cartTag).PRE_REGISTRATION_PAYMENT_TERMS,
    {
      params: {
        deviceModelId: deviceModelId,
      },
    }
  );
};

export const getManufacturer = async (
  cartTag: string
): Promise<AxiosResponse<IManufacturerWrap>> => {
  return await jwtApiClient.get(M3_API(cartTag).MANUFACTURER);
};

export const getCart = async (
  cartTag: string
): Promise<AxiosResponse<ICartWrap>> => {
  return await jwtApiClient.get(M3_API(cartTag).ORDER, {
    headers: {
      Prefer: "code=200, example=default",
    },
  });
};

export const createCart = async (
  productVersion = ProductVersion.M3
): Promise<AxiosResponse<ICartWrap>> => {
  return await jwtApiClient.post(
    M3_API().CREATE_CART,
    {
      productVersion: productVersion,
    },
    {
      headers: {
        Prefer: "code=200, example=default",
      },
      params: {
        "include-user": 1,
      },
    }
  );
};

export const cancelCart = async (
  cartTag: string,
  step: string,
  reason: string
): Promise<AxiosResponse<unknown>> => {
  return await jwtApiClient.put(
    M3_API(cartTag).CANCEL_CART,
    {
      step: step,
      reason: reason,
    },
    {
      params: {
        cartTag,
      },
    }
  );
};

export const updateDeviceModel = async (
  cartTag: string,
  model: string,
  description?: string
): Promise<AxiosResponse<unknown>> => {
  return await jwtApiClient.put(
    M3_API(cartTag).SET_DEVICE_MODEL,
    {},
    {
      params: {
        model: model,
        description: description,
      },
    }
  );
};

export const setDownPayment = async (
  cartTag: string,
  downPayment?: number
): Promise<AxiosResponse<unknown>> => {
  return await jwtApiClient.put(
    M3_API(cartTag).SET_DOWN_PAYMENT,
    {},
    {
      params: { downPayment },
    }
  );
};

export const setPrice = async (
  cartTag: string,
  price?: number,
  accessoryPrice?: number | null | undefined,
  accessoryType?: ICartAccessoryType | null | undefined,
  isAccessoryRemoved?: boolean | null | undefined
): Promise<AxiosResponse<unknown>> => {
  return await jwtApiClient.put(
    M3_API(cartTag).SET_PRICE,
    {},
    {
      params: { price, accessoryPrice, accessoryType, isAccessoryRemoved },
    }
  );
};

export const getPricing = async (
  cartTag: string
): Promise<AxiosResponse<unknown>> => {
  return await jwtApiClient.get(M3_API(cartTag).PRICING);
};

export const setDevicePhone = async (
  cartTag: string,
  devicePhoneNumber: string,
  code: string,
  email: string
): Promise<AxiosResponse<unknown>> => {
  return await jwtApiClient.put(M3_API(cartTag).SET_DEVICE_PHONE, {
    cartTag,
    devicePhoneNumber: devicePhoneNumber.replace(/\D+/g, ""),
    code,
    email,
  });
};

export const setImei = async (
  cartTag: string,
  imei: string
): Promise<AxiosResponse<ICartWrap>> => {
  return await jwtApiClient.put(M3_API(cartTag).SET_DEVICE_IMEI, {
    cartTag,
    imei,
  });
};

export const setDevice = async (
  cartTag: string,
  imei: string | null,
  deviceTag: string | null
): Promise<AxiosResponse<ICartWrap>> => {
  return await jwtApiClient.put(M3_API(cartTag).SET_DEVICE, {
    imei,
    deviceTag,
  });
};

export const submitCart = async (
  cartTag: string,
  deviceTag: string,
  imei?: string,
  serialNumber?: string
): Promise<AxiosResponse<ISubmitCartResponse>> => {
  return await jwtApiClient.post(
    M3_API().SUBMIT_CART,
    {},
    {
      params: {
        deviceTag,
        cartTag,
        imei,
        serialNumber,
      },
    }
  );
};

export const updatePage = async (
  cartTag: string,
  page: string
): Promise<AxiosResponse> => {
  return await jwtApiClient.put(M3_API(cartTag).UPDATE_PAGE, {
    page: page,
  });
};

export const getCartStatus = async (
  cartTag: string
): Promise<AxiosResponse<ICartStatus>> => {
  return await jwtApiClient.get(M3_API(cartTag).CART_STATUS, {});
};

export const isCsAvailable = async (
  cartTag: string
): Promise<AxiosResponse<{ available: string | boolean | null }>> => {
  return await jwtApiClient.get(M3_API(cartTag).IS_CS_AVAILABLE, {});
};

export const getNextRoute = async (
  cartTag: string,
  callerPage: string
): Promise<AxiosResponse<ICartStatus>> => {
  return await jwtApiClient.get(
    M3_API(cartTag).GET_NEXT_ROUTE + "/" + callerPage
  );
};

export const getFraudCheck = async (
  cartTag: string
): Promise<AxiosResponse<IFraudCheckResponse>> => {
  return await jwtApiClient.get(M3_API(cartTag).GET_FRAUD_CHECK);
};

export const getIncodeLiveness = async (
  cartTag: string
): Promise<AxiosResponse<IIncodeLivenessResponse>> => {
  return await jwtApiClient.get(M3_API(cartTag).GET_INCODE_LIVENESS);
};

export const getPhoneSelfieStatus = async (
  cartTag: string
): Promise<AxiosResponse<IGetPhoneSelfieStatusResponse>> => {
  return await jwtApiClient.get(M3_API(cartTag).GET_PHONE_SELFIE_STATUS);
};
